import { JSX } from "preact";
import { useEffect, useRef } from "preact/hooks";
import type { Message } from "../widget";

interface Props {
  messages: Message[];
  loading: boolean;
}

export function MessageList({ messages, loading }: Props): JSX.Element {
  const endRef = useRef<HTMLDivElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Scroll on count change AND on text growth (streaming). Using the last
  // message's text length as a cheap "did content change" signal avoids a
  // deep array compare while still firing on every appended delta.
  //
  // Use "auto" (instant) scroll during streaming: "smooth" creates queued
  // animations that visibly lag behind growing text, which is the glitchy
  // feel we want to fix. Smooth scroll is fine on first-message-appearance,
  // but during a stream we want the bottom to track in lockstep.
  //
  // We also respect user intent: if they've scrolled up to read earlier
  // messages (i.e. they're not near the bottom), don't yank them back down.
  const lastTextLen = messages.length > 0
    ? messages[messages.length - 1].text.length
    : 0;
  useEffect(() => {
    const c = containerRef.current;
    if (!c) return;
    // Threshold: if the visitor scrolled up more than 80px from the bottom,
    // they're reading. Don't fight them.
    const distanceFromBottom = c.scrollHeight - c.scrollTop - c.clientHeight;
    if (distanceFromBottom < 80) {
      // Scroll the messages CONTAINER directly — NOT scrollIntoView(), which
      // scrolls every scrollable ancestor (i.e. the host page jumps when the
      // widget is embedded inline). This keeps scrolling inside the widget.
      c.scrollTop = c.scrollHeight;
    }
  }, [messages.length, lastTextLen, loading]);

  return (
    <div class="acc-messages" role="log" aria-live="polite" aria-relevant="additions" ref={containerRef}>
      {messages.map((m, i) => {
        if (m.role === "system") {
          // System pseudo-messages are presence/status notices like
          // "Jas joined the chat". Rendered centered + muted.
          return (
            <div key={m.id} class="acc-msg-system">
              <span>{m.text}</span>
            </div>
          );
        }
        // Wider gap only on a speaker change; tight within a same-sender run.
        const turnChange = i > 0 && messages[i - 1].role !== m.role;
        return (
          <div
            key={m.id}
            class={
              `acc-msg acc-msg-${m.role}` +
              (turnChange ? " acc-msg--turn" : "") +
              (m.voice ? " acc-msg-voice" : "") +
              (m.interim ? " acc-msg-interim" : "")
            }
          >
            {/* dir="auto" so a reply in Arabic/Hebrew aligns RTL inside the bubble. */}
            <div class="acc-bubble" dir="auto">{m.text}</div>
          </div>
        );
      })}
      {loading && (
        <div class="acc-msg acc-msg-assistant">
          <div class="acc-bubble acc-thinking" aria-label="Typing">
            <span class="acc-typing-dots">
              <span></span><span></span><span></span>
            </span>
          </div>
        </div>
      )}
      <div ref={endRef} />
    </div>
  );
}
